import { FormationDocumentPageContent, PDFFormPageContent } from '@st/formation'
import {
  HTMLRenderedPage,
  renderToRenderedPage,
  type RenderedAnnotation
} from '@st/document-rendering'
import {
  type DocumentViewerSend,
  DocumentViewport,
  Layer,
  Page,
  type ResolvedPageStructure
} from '@st/document-viewer-kit'
import { memo } from 'react'
import { DocViewerScaffold, PDFDocToolbar } from '~/st-doc-viewer'
import { commentsSidebarPanel } from '~/st-doc-viewer/plugins/comment/sidebar'
import { StatusStampsDocumentBanner } from '~/st-doc-viewer/plugins/sign-offs/ui'
import { AnnotationsOverlay } from '~/st-doc-viewer/runtime-definition'
import { ORGANIZER_ANNOTATE_TOOLBAR_FEATURES } from '~/st-doc-viewer/toolbar-features'
import { useProcess, useSelect } from '~/store'
import { useSendViewerMessage } from './st-folder-hooks'
import type { SidebarVisibility } from '~/st-doc-viewer/sidebar-layout'
import { STAnnotationsProvider } from './st-annotations-provider'
import type { STFolderMessage } from './st-folder-module'
import {
  selViewerActiveAnnotation,
  selSidebarVisibility,
  selViewerAnnotationsForPage,
  selViewerAnnotationState
} from './st-folder-selectors'
import { selFormationPageView, selFormationViewerView } from './st-formation-selectors'

type FormationViewerView = NonNullable<ReturnType<typeof selFormationViewerView>>

/*
 * Composition root only: select the already-derived view model, acquire the
 * process handle, and translate DVK messages into folder messages. Formation
 * policy and state transitions belong in selectors and the module, not here.
 */
export function STFormationViewer() {
  const view = useSelect('stFolder', selFormationViewerView)
  const process = useProcess('stFolder')
  const sendViewerMessage = useSendViewerMessage(process.send)

  if (!view) return null

  const content = (
    <FormationViewer view={view} send={process.send} sendViewerMessage={sendViewerMessage} />
  )
  if (!view.annotationsEnabled) return content

  return <STAnnotationsProvider>{content}</STAnnotationsProvider>
}

function FormationViewer({
  view,
  send,
  sendViewerMessage
}: {
  view: FormationViewerView
  send: (message: STFolderMessage) => void
  sendViewerMessage: DocumentViewerSend
}) {
  const sidebarVisibility = useSelect('stFolder', selSidebarVisibility)
  let toolbar
  let banner
  let sidebar

  if (view.annotationsEnabled) {
    toolbar = (
      <PDFDocToolbar
        sendViewerMessage={sendViewerMessage}
        features={ORGANIZER_ANNOTATE_TOOLBAR_FEATURES}
      />
    )
    banner = (
      <StatusStampsDocumentBanner title={view.documentName} sendViewerMessage={sendViewerMessage} />
    )
  }
  if (view.showCommentsSidebar) {
    sidebar = {
      panel: commentsSidebarPanel(sendViewerMessage),
      visibility: sidebarVisibility,
      onVisibilityChange: (visibility: SidebarVisibility) =>
        send({ type: 'sidebarVisibilityChanged', visibility })
    }
  }

  return (
    <DocViewerScaffold toolbar={toolbar} banner={banner} sidebar={sidebar}>
      <DocumentViewport
        send={sendViewerMessage}
        status={view.viewport.pagesStatus}
        showPages={view.viewport.showPages}
        pagesToRender={view.viewport.pagesToRender}
        pageTransform={view.viewport.pageTransform}
      >
        {view.pages.map((page) => (
          <FormationViewerPage
            key={page.structure.pageNumber}
            send={send}
            sendViewerMessage={sendViewerMessage}
            structure={page.structure}
            highlight={page.highlight}
            annotationsEnabled={view.annotationsEnabled}
          />
        ))}
      </DocumentViewport>
    </DocViewerScaffold>
  )
}

const FormationViewerPage = memo(function FormationViewerPage({
  send,
  sendViewerMessage,
  structure,
  highlight,
  annotationsEnabled
}: {
  send: (message: STFolderMessage) => void
  sendViewerMessage: DocumentViewerSend
  structure: ResolvedPageStructure
  highlight: boolean
  annotationsEnabled: boolean
}) {
  const view = useSelect('stFolder', selFormationPageView, structure.pageNumber)

  // Page content is expensive because it runs the custom document reconciler.
  // Each page subscribes to its own derived model, so viewport changes and
  // edits to other pages leave this subtree mounted without a React-level
  // comparator duplicating the store's equality policy.
  const renderedPage = renderToRenderedPage(
    <PDFFormPageContent width={650} height={1000}>
      <FormationDocumentPageContent
        view={view}
        highlight={highlight}
        onFieldChange={(key, value) => send({ type: 'formationFieldChanged', key, value })}
      />
    </PDFFormPageContent>,
    { width: 650, height: 1000 }
  )
  let shouldRenderAnnotation
  if (annotationsEnabled) {
    shouldRenderAnnotation = shouldRenderFormationHTMLAnnotation
  }

  return (
    <Page
      page={structure}
      topDivider={structure.pageNumber == 1 ? null : undefined}
      bottomDivider={null}
    >
      <Layer>
        <HTMLRenderedPage
          page={renderedPage}
          width={650}
          height={1000}
          shouldRenderAnnotation={shouldRenderAnnotation}
        />
      </Layer>

      {annotationsEnabled && (
        <FormationAnnotationsPageOverlay
          send={sendViewerMessage}
          page={structure}
          placements={renderedPage.annotations}
        />
      )}
    </Page>
  )
})

function FormationAnnotationsPageOverlay({
  send,
  page,
  placements
}: {
  send: DocumentViewerSend
  page: ResolvedPageStructure
  placements: RenderedAnnotation[]
}) {
  const annotationState = useSelect('stFolder', selViewerAnnotationState)
  const activeAnnotation = useSelect('stFolder', selViewerActiveAnnotation)
  const annotations = useSelect('stFolder', selViewerAnnotationsForPage, page.pageNumber)

  return (
    <Layer>
      <AnnotationsOverlay
        send={send}
        page={page}
        annotations={annotations}
        placements={placements}
        annotationState={annotationState}
        activeAnnotation={activeAnnotation}
        renderToolOverlay={true}
      />
    </Layer>
  )
}

function shouldRenderFormationHTMLAnnotation(annotation: RenderedAnnotation) {
  return annotation.type != 'comment'
}
